import { isDefined } from "@clipboard-health/util-ts";
import { useEmbeddedMode } from "@src/appV2/EmbeddedMode/EmbeddedModeContext";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { useHistory, useLocation } from "react-router-dom";

import { type PlacementWithDetails } from "../../../api/useFetchPaginatedPlacements";
import { PLACEMENT_PATHS } from "../../../paths";

interface PlacementDetailsPageHeaderProps {
  placement: PlacementWithDetails;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  onFavoritePlacementChange: () => Promise<void>;
  isUpdatingFavoritedStatus: boolean;
}

export function PlacementDetailsPageHeader(props: PlacementDetailsPageHeaderProps) {
  const { placement, scrollContainerRef, isUpdatingFavoritedStatus, onFavoritePlacementChange } =
    props;
  const history = useHistory();
  const location = useLocation();
  const { isEmbedded } = useEmbeddedMode();

  function handleBackClick() {
    // Only use browser history when all three conditions are met:
    // 1. Not in embedded mode (embedded history includes auth/external pages that would exit the iframe)
    // 2. Location key exists (confirms we navigated here via the router, not a manual URL entry)
    // 3. Meaningful history depth (history.length <= 2 means fresh deep link with nowhere useful to go back to)
    // Otherwise, navigate to the Jobs feed as a safe default.
    if (!isEmbedded && isDefined(location.key) && history.length > 2) {
      history.goBack();
    } else {
      history.replace(PLACEMENT_PATHS.ROOT);
    }
  }

  const { title, isFavorited } = placement;

  return (
    <StickyScrollPageHeader
      shouldWrapTitle
      headerVariant="tertiary"
      title={title}
      scrollContainerRef={scrollContainerRef}
      rightAction={
        <IconButton
          disabled={isUpdatingFavoritedStatus}
          iconType={isFavorited ? "like-filled" : "like-outlined"}
          variant="outlined"
          onClick={onFavoritePlacementChange}
        />
      }
      onClickBack={() => {
        handleBackClick();
      }}
    />
  );
}
